import { useCallback, useEffect, useState, useRef } from 'react';
import { getClientsPaged } from '../../services/clientService';

export default function useGetClientsPaged(
  pageIndex,
  pageSize,
  clientSearch,
  filters = {},
  sortBy = null,
  sortDesc = false
) {
  const [clients, setClients] = useState([]);
  const [clientsCount, setClientsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const refetch = useCallback(
    async (overrides = {}) => {
      try {
        setLoading(true);
        const page = overrides.page ?? pageIndex + 1;
        const pageSizeArg = overrides.pageSize ?? pageSize;
        const clientSearchArg = overrides.clientSearch ?? clientSearch;

        abortControllerRef.current = new AbortController();
        const params = {
          page,
          pageSize: pageSizeArg,
          q: clientSearchArg,
          ...filters,
        };

        if (sortBy) {
          params.sortBy = sortBy;
          params.desc =
            sortDesc === null || sortDesc === undefined ? true : sortDesc;
        }

        const data = await getClientsPaged(
          params,
          abortControllerRef.current.signal
        );

        const mappedClients = (data.items || []).map((item) => ({
          id: item.id,
          client: item.name,
          rut: item.identifier,
          phone: item.telephone,
          email: item.email,
          sac: item.sac,
          office: item.branchName || item.branchName2,
          state: item.active ? 'Activo' : 'Inactivo',
          category: item.category,
        }));

        setClients(mappedClients);
        setClientsCount(data.total ?? 0);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error en refetch:', err);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [pageIndex, pageSize, clientSearch, filters, sortBy, sortDesc]
  );

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    debounceTimerRef.current = setTimeout(() => {
      refetch();
    }, 300);

    return () => {
      clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, [refetch]);

  return { clients, clientsCount, loading, error, refetch };
}
